import { router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import { EditableCell } from "@/components/bids/EditableCell"
import { GmChip } from "@/components/bids/GmChip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "@/components/ui/table"
import { EM_DASH, money, pct, rate } from "@/lib/money"
import type { WorkspaceCategory, WorkspaceLevel, WorkspaceProduct } from "@/types/bids"

/** The lineup's cost build-up & quoted price — the design's audit table.
 *  Every money column is server-computed; the two editable numbers here are
 *  the builder's code and the material cost override. */
export function CategoryPricingTable({ category }: { category: WorkspaceCategory }) {
  const levelsById = new Map<number, WorkspaceLevel>(category.levels.map((l) => [l.id, l]))

  if (category.products.length === 0) return null

  return (
    <div data-testid={`pricing-table-${category.key}`}>
      <Table>
        <TableHead>
          <tr>
            <TableHeadCell>Item #</TableHeadCell>
            <TableHeadCell>Style</TableHeadCell>
            <TableHeadCell>Level</TableHeadCell>
            <TableHeadCell className="text-right">Material</TableHeadCell>
            <TableHeadCell className="text-right">Freight</TableHeadCell>
            <TableHeadCell className="text-right">Labor</TableHeadCell>
            <TableHeadCell className="text-right">Use tax</TableHeadCell>
            <TableHeadCell className="text-right">COGS</TableHeadCell>
            <TableHeadCell className="text-right">Tgt GM</TableHeadCell>
            <TableHeadCell className="text-right">Tgt price</TableHeadCell>
            <TableHeadCell className="text-right">Quoted</TableHeadCell>
            <TableHeadCell className="text-right">GM</TableHeadCell>
            <TableHeadCell className="w-12" />
          </tr>
        </TableHead>
        <TableBody>
          {category.products.map((product) => {
            const level = levelsById.get(product.level_id)
            return (
              <TableRow key={product.id} data-testid={`pricing-row-${product.id}`}>
                <TableCell>
                  <EditableCell
                    value={product.builder_code ?? ""}
                    display={product.builder_code ?? EM_DASH}
                    onCommit={(raw) =>
                      router.patch(product.url, { bid_lineup_product: { builder_code: raw } }, { preserveScroll: true })
                    }
                    testid={`builder-code-${product.id}`}
                    className="font-mono text-xs"
                  />
                </TableCell>
                <TableCell>
                  <span className="text-ink-display">
                    {[product.item.style, product.item.format_label].filter(Boolean).join(" · ") ||
                      product.item.id}
                  </span>
                  {product.item.color && (
                    <span className="block text-xs text-ink-muted">{product.item.color}</span>
                  )}
                </TableCell>
                <TableCell>{level?.name ?? EM_DASH}</TableCell>
                <TableCell className="text-right">
                  <MaterialCell product={product} />
                </TableCell>
                <TableCell className="text-right">
                  <EditableCell
                    value={product.freight_cost}
                    display={rate(product.freight_cost)}
                    suffix={product.item.unit === "SY" ? "/SY" : undefined}
                    onCommit={(raw) =>
                      router.patch(product.url, { bid_lineup_product: { freight_cost: raw } }, { preserveScroll: true })
                    }
                    testid={`freight-cost-${product.id}`}
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums">{rate(product.econ.labor)}</TableCell>
                <TableCell className="text-right tabular-nums">{rate(product.econ.tax)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {money(product.econ.cogs)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {level ? pct(level.target_gm_pct) : EM_DASH}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(product.econ.target_price)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {level ? `${money(level.quoted_price)}/${level.unit_label}` : EM_DASH}
                </TableCell>
                <TableCell className="text-right">
                  <GmChip
                    actual={product.econ.gm_pct}
                    target={level?.target_gm_pct ?? null}
                    testid={`gm-chip-${product.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => router.delete(product.url, { preserveScroll: true })}
                    aria-label="Remove from lineup"
                    data-testid={`remove-product-${product.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

function MaterialCell({ product }: { product: WorkspaceProduct }) {
  if (product.econ.uncosted) {
    return <Badge tone="signal">uncosted</Badge>
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <EditableCell
        value={product.material_cost}
        display={rate(product.material_cost)}
        suffix={product.item.unit === "SY" ? "/SY" : undefined}
        onCommit={(raw) =>
          router.patch(product.url, { bid_lineup_product: { material_cost: raw } }, { preserveScroll: true })
        }
        testid={`material-cost-${product.id}`}
      />
      {product.cost_updated_at == null && <Badge tone="signal">no cost</Badge>}
    </span>
  )
}
